import type { UFPMMember } from './types'
import type { UFPMHistoryEntry } from '../composables/useUFPMHistory'
import { componentWasCompleted } from './scoreHistoryEntry'
import { saveBlob } from './ufpmPlanExport'

export const ROSTER_EXPORT_HEADERS = [
  'Organization Name',
  'Rank',
  'Last Name',
  'First Name',
  'Fitness Currency',
  'Composite Score',
  'Latest Fitness Assessment Category',
  'Latest Fitness Assessment Completion',
  'Next Fitness Assessment Due Date',
] as const

export const HISTORY_DETAIL_HEADERS = [
  'PU Completed',
  'PU Score',
  'PU Exempt',
  'PU Type',
  'PU Result',
  'Core Completed',
  'Core Score',
  'Core Exempt',
  'Core Type',
  'Core Result',
  'Cardio Completed',
  'Cardio Score',
  'Cardio Exempt',
  'Cardio Type',
  'Cardio Result',
  'WHtR Completed',
  'WHtR Score',
  'WHtR Exempt',
  'WHtR Result',
] as const

export const HISTORY_EXPORT_HEADERS = [...ROSTER_EXPORT_HEADERS, ...HISTORY_DETAIL_HEADERS]

function yesNo(value: boolean | undefined): string {
  return value ? 'Yes' : 'No'
}

function scoreCell(value: string | number | undefined, exempt?: boolean): string {
  if (exempt) return 'EXM'
  if (value == null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? n.toFixed(1) : String(value)
}

function memberForEntry(members: readonly UFPMMember[], entry: UFPMHistoryEntry): UFPMMember | null {
  return members.find(m => m.id === entry.memberId) ?? null
}

export function historyEntryToRow(
  entry: UFPMHistoryEntry,
  member: UFPMMember | null,
): (string | number)[] {
  const testDate = entry.recordedDate || entry.date || ''
  const composite = entry.composite != null && entry.composite !== ''
    ? Number(entry.composite).toFixed(1)
    : ''

  return [
    member?.organization ?? '',
    member?.rank ?? '',
    member?.lastName ?? '',
    member?.firstName ?? '',
    member?.fitnessCurrency ?? '',
    composite,
    entry.cat ?? '',
    testDate,
    member?.nextFitnessAssessmentDueDate ?? '',
    yesNo(componentWasCompleted(entry, 'pu')),
    scoreCell(entry.puScore, entry.puEx),
    yesNo(entry.puEx),
    entry.puType ?? '',
    entry.puDetail ?? '',
    yesNo(componentWasCompleted(entry, 'core')),
    scoreCell(entry.coreScore, entry.coreEx),
    yesNo(entry.coreEx),
    entry.coreType ?? '',
    entry.coreDetail ?? '',
    yesNo(componentWasCompleted(entry, 'cardio')),
    scoreCell(entry.cardioScore, entry.cardioEx),
    yesNo(entry.cardioEx),
    entry.cardioType ?? '',
    entry.cardioDetail ?? '',
    yesNo(componentWasCompleted(entry, 'whtr')),
    scoreCell(entry.whtrScore, entry.whtrEx),
    yesNo(entry.whtrEx),
    entry.whtrDetail ?? '',
  ]
}

export function buildHistoryExportRows(
  entries: readonly UFPMHistoryEntry[],
  members: readonly UFPMMember[],
): (string | number)[][] {
  return entries.map(entry => historyEntryToRow(entry, memberForEntry(members, entry)))
}

function exportFilename(dateFrom: string, dateTo: string): string {
  const base = 'ufpm-test-history'
  if (dateFrom && dateTo && dateFrom !== dateTo) {
    return `${base}-${dateFrom}_to_${dateTo}.xlsx`
  }
  if (dateFrom) return `${base}-${dateFrom}.xlsx`
  return `${base}-all-dates.xlsx`
}

export function exportHistoryXlsx(
  entries: readonly UFPMHistoryEntry[],
  members: readonly UFPMMember[],
  dateFrom = '',
  dateTo = '',
): void {
  if (!entries.length) {
    window.showToast?.('No test results match the selected date range.')
    return
  }

  const xlsx = (window as unknown as {
    XLSX?: {
      utils: {
        book_new: () => unknown
        aoa_to_sheet: (rows: unknown[][]) => Record<string, unknown>
        book_append_sheet: (wb: unknown, ws: unknown, name: string) => void
      }
      write: (wb: unknown, options: { bookType: 'xlsx'; type: 'array' }) => ArrayBuffer
    }
  }).XLSX

  if (!xlsx?.utils || !xlsx.write) {
    window.showToast?.('Excel export is unavailable in this build.')
    return
  }

  const wb = xlsx.utils.book_new()
  const rows: (string | number)[][] = [HISTORY_EXPORT_HEADERS.slice(), ...buildHistoryExportRows(entries, members)]
  const ws = xlsx.utils.aoa_to_sheet(rows)
  ws['!cols'] = [
    { wch: 28 }, { wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
    { wch: 14 }, { wch: 28 }, { wch: 14 }, { wch: 14 },
    { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 18 },
    { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 18 },
    { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 18 },
    { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 18 },
  ]
  ws['!autofilter'] = { ref: `A1:AB${rows.length}` }
  ws['!freeze'] = { xSplit: 0, ySplit: 1 }
  xlsx.utils.book_append_sheet(wb, ws, 'Test History')

  const data = xlsx.write(wb, { bookType: 'xlsx', type: 'array' })
  saveBlob(
    exportFilename(dateFrom, dateTo),
    new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ).catch(() => window.showToast?.('Excel export failed. Please try again.'))
}